import { supabase } from "@/integrations/supabase/client";
import type { SuburbRecord } from "./types";

export interface ValuationAlgorithm {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  version: number;
  status: "active" | "disabled" | "todo";
}

export interface ValuationRun {
  id: string;
  algorithm_id: string;
  status: "running" | "completed" | "failed";
  progress: number;
  suburb_id: string | null;
  suburb_name: string;
  postcode: string;
  state: string;
  property_type: string;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  months: number;
  filters: Record<string, unknown>;
  result_summary: Record<string, unknown>;
  error_message: string | null;
  created_at: string;
  started_at: string;
  completed_at: string | null;
  valuation_algorithms?: Pick<ValuationAlgorithm, "slug" | "name" | "version"> | null;
}

export interface ValuationSuburbMonthlyResult {
  id: string;
  run_id: string;
  sale_month: string;
  average_price: number | null;
  median_price: number | null;
  min_price: number | null;
  max_price: number | null;
  sample_count: number;
  raw_sample_count: number;
  outliers_removed: number;
  previous_average_price: number | null;
  price_change: number | null;
  percentage_change: number | null;
}

export interface RunSuburbMonthlyMovementParams {
  suburbId: string;
  months?: number;
  bedrooms?: number | null;
  bathrooms?: number | null;
  parking?: number | null;
}

export interface RunSuburbMonthlyMovementResponse {
  success: boolean;
  runId?: string;
  results?: Array<Record<string, unknown>>;
  summary?: Record<string, unknown>;
  error?: string;
}

export const DEFAULT_VALUATION_ALGORITHM: ValuationAlgorithm = {
  id: "local-pv-suburb-movements-monthly-v1",
  slug: "pv_suburb_movements_monthly_v1",
  name: "PV Suburb Movements Monthly",
  description: "Monthly suburb-level house price movement from internal sale snapshots.",
  version: 1,
  status: "active",
};

function sanitizeSearchTerm(value: string): string {
  return value.replace(/[%,()]/g, "").trim();
}

export async function fetchValuationAlgorithms(): Promise<ValuationAlgorithm[]> {
  const { data, error } = await (supabase as any)
    .from("valuation_algorithms")
    .select("id,slug,name,description,version,status")
    .eq("status", "active")
    .order("name");

  if (error) {
    throw error;
  }

  return data ?? [];
}

export async function fetchDefaultValuationSuburb(): Promise<SuburbRecord | null> {
  const { data, error } = await (supabase as any)
    .from("suburbs")
    .select("*")
    .eq("postcode", "3204")
    .or("locality.ilike.%McKinnon%,suburb_name.ilike.%McKinnon%")
    .limit(1)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data ?? null;
}

export async function searchValuationSuburbs(search: string): Promise<SuburbRecord[]> {
  const term = sanitizeSearchTerm(search);
  if (term.length < 2) {
    return [];
  }

  const { data, error } = await (supabase as any)
    .from("suburbs")
    .select("*")
    .or(`locality.ilike.%${term}%,suburb_name.ilike.%${term}%,postcode.ilike.${term}%`)
    .order("state")
    .order("locality")
    .limit(20);

  if (error) {
    throw error;
  }

  return data ?? [];
}

export async function fetchValuationRuns(limit = 12): Promise<ValuationRun[]> {
  const { data, error } = await (supabase as any)
    .from("valuation_runs")
    .select("*, valuation_algorithms(slug,name,version)")
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) {
    throw error;
  }

  return data ?? [];
}

export async function fetchValuationRunResults(
  runId: string,
): Promise<ValuationSuburbMonthlyResult[]> {
  const { data, error } = await (supabase as any)
    .from("valuation_suburb_monthly_results")
    .select("*")
    .eq("run_id", runId)
    .order("sale_month", { ascending: true });

  if (error) {
    throw error;
  }

  return data ?? [];
}

export async function runSuburbMonthlyMovement(
  params: RunSuburbMonthlyMovementParams,
): Promise<RunSuburbMonthlyMovementResponse> {
  const { data, error } = await supabase.functions.invoke("run-valuation-algorithm", {
    body: {
      action: "run-suburb-movements-monthly",
      suburbId: params.suburbId,
      months: params.months ?? 12,
      bedrooms: params.bedrooms ?? null,
      bathrooms: params.bathrooms ?? null,
      parking: params.parking ?? null,
    },
  });

  if (error) {
    throw error;
  }

  return data as RunSuburbMonthlyMovementResponse;
}
